import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import api from '../services/api';
import { useAuth } from './AuthContext';
import { useOperationalTab } from './OperationalTabContext';
import type { UserTelephonyAvailability } from '../types';

const OPERATOR_LIVE_PRESENCE_HEARTBEAT_INTERVAL_MS = 15_000;

type OperatorReadinessState = NonNullable<UserTelephonyAvailability['readinessState']>;
type OperatorReadinessEffectiveStatus = NonNullable<UserTelephonyAvailability['effectiveStatus']>;

interface OperatorLivePresenceContextValue {
  readinessState: OperatorReadinessState | null;
  isConfirmingReadiness: boolean;
  confirmReadyForTransfers: () => Promise<void>;
}

const OperatorLivePresenceContext = createContext<OperatorLivePresenceContextValue | undefined>(undefined);

function isOperatorLivePresenceEligible(endpoint: {
  isActive?: boolean;
} | null | undefined): boolean {
  return Boolean(endpoint?.isActive);
}

function buildKeepaliveUrl(pathname: string): string {
  const baseUrl = String(api.defaults.baseURL || '').trim();
  if (!baseUrl) {
    return pathname;
  }
  return `${baseUrl}${pathname}`;
}

function buildKeepaliveHeaders(token: string): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  const switchedTenantId = window.localStorage.getItem('switchedTenantId');
  if (switchedTenantId) {
    headers['X-Tenant-Id'] = switchedTenantId;
  }
  return headers;
}

function currentIsoTimestamp(): string {
  return new Date().toISOString();
}

function resolveDocumentVisibilityState(): 'visible' | 'hidden' {
  return document.visibilityState === 'hidden' ? 'hidden' : 'visible';
}

function resolveDocumentHasFocus(): boolean {
  return typeof document.hasFocus === 'function' ? document.hasFocus() : true;
}

function effectiveStatusFromReadiness(
  availability: UserTelephonyAvailability,
  readinessState: OperatorReadinessState | null,
): OperatorReadinessEffectiveStatus {
  if (!availability.isEligible) {
    return availability.effectiveStatus || 'no_telephony';
  }
  if (availability.status !== 'available') {
    return 'unavailable';
  }
  if (readinessState === 'ready') {
    return 'available';
  }
  if (readinessState === 'idle') {
    return 'operator_idle';
  }
  if (readinessState === 'hidden') {
    return 'operator_hidden';
  }
  return 'operator_needs_confirmation';
}

/**
 * Maintain the short-lived operator browser presence heartbeat.
 *
 * This provider is intentionally side-effect-only. The operational contract is:
 * - only the leader tab may emit presence heartbeats;
 * - the browser liveness signal is short-lived and Redis-backed;
 * - best-effort stop on page leave complements, but never replaces, TTL expiry.
 */
export function OperatorLivePresenceProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, token, sessionId, user, updateUser } = useAuth();
  const { canOperateInThisTab, isReady, tabId } = useOperationalTab();
  const heartbeatInFlightCountRef = useRef(0);
  const heartbeatRequestSeqRef = useRef(0);
  const latestAppliedHeartbeatSeqRef = useRef(0);
  const lastHumanActivityAtRef = useRef<string | null>(null);
  const telephonyAvailabilityRef = useRef<UserTelephonyAvailability | null>(
    user?.telephonyAvailability ?? null,
  );
  const [readinessState, setReadinessState] = useState<OperatorReadinessState | null>(
    user?.telephonyAvailability?.readinessState ?? null,
  );
  const [isConfirmingReadiness, setIsConfirmingReadiness] = useState(false);

  const endpointEligible = useMemo(
    () => isOperatorLivePresenceEligible(user?.telephonyEndpoint),
    [user?.telephonyEndpoint],
  );
  const isEligible = Boolean(
    isAuthenticated
      && token
      && sessionId
      && tabId
      && isReady
      && canOperateInThisTab
      && endpointEligible,
  );

  useEffect(() => {
    telephonyAvailabilityRef.current = user?.telephonyAvailability ?? null;
    setReadinessState(user?.telephonyAvailability?.readinessState ?? null);
  }, [user?.telephonyAvailability]);

  const applyReadinessSnapshot = useCallback((nextReadinessState: OperatorReadinessState | null, payload: {
    lastHumanActivityAt?: string | null;
    readyConfirmedAt?: string | null;
    browserVisibilityState?: 'visible' | 'hidden';
    documentHasFocus?: boolean;
    effectiveStatus?: OperatorReadinessEffectiveStatus | null;
  } = {}) => {
    setReadinessState(nextReadinessState);
    const currentAvailability = telephonyAvailabilityRef.current;
    if (!currentAvailability || !nextReadinessState) {
      return;
    }

    const nextEffectiveStatus = payload.effectiveStatus
      ?? effectiveStatusFromReadiness(currentAvailability, nextReadinessState);
    const nextAvailability: UserTelephonyAvailability = {
      ...currentAvailability,
      readinessState: nextReadinessState,
      lastHumanActivityAt: payload.lastHumanActivityAt ?? currentAvailability.lastHumanActivityAt ?? null,
      readyConfirmedAt: nextReadinessState === 'ready'
        ? payload.readyConfirmedAt ?? currentAvailability.readyConfirmedAt ?? null
        : null,
      browserVisibilityState: payload.browserVisibilityState ?? currentAvailability.browserVisibilityState ?? null,
      documentHasFocus: payload.documentHasFocus ?? currentAvailability.documentHasFocus ?? null,
      isAvailable: currentAvailability.status === 'available' && nextEffectiveStatus === 'available',
      effectiveStatus: nextEffectiveStatus,
    };
    telephonyAvailabilityRef.current = nextAvailability;
    updateUser({ telephonyAvailability: nextAvailability });
  }, [updateUser]);

  const sendHeartbeat = useCallback(async (
    reason: string,
    options: {
      lastHumanActivityAt?: string | null;
      readyConfirmedAt?: string | null;
      force?: boolean;
    } = {},
  ) => {
    if (!isEligible || !sessionId || !tabId || (!options.force && heartbeatInFlightCountRef.current > 0)) {
      return;
    }
    const requestSeq = heartbeatRequestSeqRef.current + 1;
    heartbeatRequestSeqRef.current = requestSeq;
    heartbeatInFlightCountRef.current += 1;
    const browserVisibilityState = resolveDocumentVisibilityState();
    const documentHasFocus = resolveDocumentHasFocus();
    const lastHumanActivityAt = options.lastHumanActivityAt ?? lastHumanActivityAtRef.current;
    try {
      const response = await api.post('/api/auth/me/operator-live-presence/heartbeat/', {
        sessionId,
        tabId,
        reason,
        lastHumanActivityAt,
        readyConfirmedAt: options.readyConfirmedAt ?? undefined,
        browserVisibilityState,
        documentHasFocus,
      });
      const nextReadinessState = String(
        response.data?.readinessState || response.data?.readiness_state || '',
      ).trim() as OperatorReadinessState;
      const nextEffectiveStatus = String(
        response.data?.effectiveStatus || response.data?.effective_status || '',
      ).trim() as OperatorReadinessEffectiveStatus;
      if (requestSeq < latestAppliedHeartbeatSeqRef.current) {
        return;
      }
      latestAppliedHeartbeatSeqRef.current = requestSeq;
      applyReadinessSnapshot(nextReadinessState || null, {
        lastHumanActivityAt,
        readyConfirmedAt: options.readyConfirmedAt ?? undefined,
        browserVisibilityState,
        documentHasFocus,
        effectiveStatus: nextEffectiveStatus || null,
      });
    } catch {
      // Best-effort heartbeat. The TTL window and reconciler tolerate transient failures.
    } finally {
      heartbeatInFlightCountRef.current = Math.max(heartbeatInFlightCountRef.current - 1, 0);
    }
  }, [applyReadinessSnapshot, isEligible, sessionId, tabId]);

  const markHumanActivity = useCallback(() => {
    lastHumanActivityAtRef.current = currentIsoTimestamp();
  }, []);

  const confirmReadyForTransfers = useCallback(async () => {
    const confirmedAt = currentIsoTimestamp();
    lastHumanActivityAtRef.current = confirmedAt;
    setIsConfirmingReadiness(true);
    try {
      await sendHeartbeat('operator_ready_confirmed', {
        lastHumanActivityAt: confirmedAt,
        readyConfirmedAt: confirmedAt,
        force: true,
      });
    } finally {
      setIsConfirmingReadiness(false);
    }
  }, [sendHeartbeat]);

  const stopPresence = useCallback(async ({ keepalive = false }: { keepalive?: boolean } = {}) => {
    if (!token || !sessionId || !tabId) {
      return;
    }

    const payload = JSON.stringify({
      session_id: sessionId,
      tab_id: tabId,
    });
    if (keepalive && typeof window.fetch === 'function') {
      try {
        await window.fetch(buildKeepaliveUrl('/api/auth/me/operator-live-presence/stop/'), {
          method: 'POST',
          keepalive: true,
          headers: buildKeepaliveHeaders(token),
          body: payload,
        });
      } catch {
        // Best-effort page-exit cleanup.
      }
      return;
    }

    try {
      await api.post('/api/auth/me/operator-live-presence/stop/', {
        sessionId,
        tabId,
      });
    } catch {
      // Best-effort shutdown. Session/logout gates still block routing immediately.
    }
  }, [sessionId, tabId, token]);

  useEffect(() => {
    if (!isEligible) {
      return undefined;
    }

    markHumanActivity();
    void sendHeartbeat('provider_mount');
    const heartbeatTimer = window.setInterval(() => {
      void sendHeartbeat('interval');
    }, OPERATOR_LIVE_PRESENCE_HEARTBEAT_INTERVAL_MS);

    const handleFocus = () => {
      markHumanActivity();
      void sendHeartbeat('focus');
    };
    const handlePageShow = () => {
      markHumanActivity();
      void sendHeartbeat('pageshow');
    };
    const handleOnline = () => {
      void sendHeartbeat('online');
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        markHumanActivity();
      }
      void sendHeartbeat('visibilitychange');
    };
    const handlePageLeave = () => {
      void stopPresence({ keepalive: true });
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('online', handleOnline);
    window.addEventListener('pagehide', handlePageLeave);
    window.addEventListener('beforeunload', handlePageLeave);
    window.addEventListener('pointerdown', markHumanActivity);
    window.addEventListener('keydown', markHumanActivity);
    window.addEventListener('touchstart', markHumanActivity, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(heartbeatTimer);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pagehide', handlePageLeave);
      window.removeEventListener('beforeunload', handlePageLeave);
      window.removeEventListener('pointerdown', markHumanActivity);
      window.removeEventListener('keydown', markHumanActivity);
      window.removeEventListener('touchstart', markHumanActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      void stopPresence();
    };
  }, [isEligible, markHumanActivity, sendHeartbeat, stopPresence]);

  const contextValue = useMemo<OperatorLivePresenceContextValue>(() => ({
    readinessState,
    isConfirmingReadiness,
    confirmReadyForTransfers,
  }), [confirmReadyForTransfers, isConfirmingReadiness, readinessState]);

  return (
    <OperatorLivePresenceContext.Provider value={contextValue}>
      {children}
    </OperatorLivePresenceContext.Provider>
  );
}

export function useOperatorLivePresence(): OperatorLivePresenceContextValue {
  const context = useContext(OperatorLivePresenceContext);
  if (!context) {
    throw new Error('useOperatorLivePresence must be used within OperatorLivePresenceProvider');
  }
  return context;
}
